import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { Appointment, AppointmentStatus, Service } from '@salonomy/types';
import { Button, Modal, Pill } from '@salonomy/ui';
import { ConfirmModal } from '../../components/ConfirmModal.js';
import { useUpdateAppointment } from '../../data/appointments.js';
import { useCancelSeries, useDetachOccurrence, useSeries } from '../../data/recurring.js';
import { chairStaff, useSalon, useStaff } from '../../data/salon.js';
import { useServices } from '../../data/services.js';
import { useAuth } from '../../lib/auth.js';
import { money } from '../../lib/format.js';
import { spanLabel } from '../../lib/time.js';
import { useToast } from '../../lib/toast.js';
import { cadenceLabel } from '../recurring/cadence.js';
import { EditSeriesModal } from '../recurring/EditSeriesModal.js';

const STATUS_LABEL: Record<AppointmentStatus, string> = {
  booked: 'Booked',
  confirmed: 'Confirmed',
  in_chair: 'In the chair',
  completed: 'Completed',
  no_show: 'No-show',
  cancelled: 'Cancelled',
};

export function AppointmentModal({
  date,
  tz,
  appt,
  service,
  onClose,
}: {
  date: string;
  tz: string;
  appt: Appointment;
  service: Service | undefined;
  onClose: () => void;
}) {
  const update = useUpdateAppointment(date);
  const navigate = useNavigate();

  function setStatus(status: AppointmentStatus) {
    update.mutate({ id: appt.id, body: { status } });
    onClose();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={appt.clientDisplayName || 'Walk-in'}
      width={440}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            onClick={() => {
              onClose();
              void navigate({ to: '/takings', search: { appt: appt.id } as never });
            }}
          >
            Check out
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Pill tone={appt.status === 'completed' ? 'success' : 'soft'}>
          {STATUS_LABEL[appt.status]}
        </Pill>
        {appt.source !== 'salon' ? (
          <Pill tone="neutral">from {appt.source.replace('_', ' ')}</Pill>
        ) : null}
        {appt.seriesId ? <Pill tone="cobalt">Recurring</Pill> : null}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ fontSize: 15, fontWeight: 600 }}>{service?.name ?? 'Service'}</div>
        <div className="muted">{spanLabel(appt.startAt, appt.endAt, tz)}</div>
        <div className="mono" style={{ fontSize: 13 }}>
          {money(appt.priceQuoted)}
          {appt.depositPaid ? ` · ${money(appt.depositPaid)} deposit paid` : ''}
        </div>
      </div>
      {appt.notes ? (
        <div
          style={{
            background: 'var(--slm-porcelain)',
            borderRadius: 10,
            padding: 12,
            fontSize: 13,
          }}
        >
          {appt.notes}
        </div>
      ) : null}

      {/* WS7 phase 3 — when this booking is one occurrence of a standing series, surface the
          recurring controls (edit just this date / edit the whole series / cancel the rest). Renders
          nothing for a normal one-off. RBAC + sealed-book scoping handled inside. */}
      {appt.seriesId ? <RecurringControls tz={tz} appt={appt} onClose={onClose} /> : null}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Button size="sm" variant="soft" onClick={() => setStatus('confirmed')}>
          Confirm
        </Button>
        <Button size="sm" variant="soft" onClick={() => setStatus('in_chair')}>
          In the chair
        </Button>
        <Button size="sm" variant="primary" onClick={() => setStatus('completed')}>
          Complete
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setStatus('no_show')}>
          No-show
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setStatus('cancelled')}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}

/**
 * The occurrence-level recurring affordance for a series booking. Three honest actions:
 *  • "Edit just this date" — detach this one occurrence from the series (useDetachOccurrence). It
 *    keeps its booking but is unlinked, so a later edit-series / cancel-remaining won't touch it;
 *    the operator then edits it with the normal appointment controls above.
 *  • "Edit the whole series" — opens EditSeriesModal (apply forward to every future occurrence).
 *  • "Cancel the rest" — cancels every future, not-yet-started occurrence and ends the series.
 *
 * Scoping mirrors RecurringSeriesPanel exactly: a manager acts on any chair's series; everyone else
 * acts only on their own (the series belongs to appt.staffId — for a sealed renter that's their own
 * book). RLS already enforces this server-side; we gate the UI to match so a chair-renter never sees
 * a control they can't use.
 */
function RecurringControls({
  tz,
  appt,
  onClose,
}: {
  tz: string;
  appt: Appointment;
  onClose: () => void;
}) {
  const seriesId = appt.seriesId!;
  const { isManager, user } = useAuth();
  const { data: salon } = useSalon();
  const { data: staff } = useStaff();
  const { data: services } = useServices();
  const { data: series } = useSeries(seriesId);
  const detach = useDetachOccurrence();
  const cancel = useCancelSeries();
  const toast = useToast();

  const seriesTz = salon?.timezone ?? tz;
  const allChairs = useMemo(() => chairStaff(staff), [staff]);

  // Manager-or-self, identical to RecurringSeriesPanel: the series belongs to appt.staffId, so a
  // non-manager may only act on their own standing booking.
  const canManage = isManager || appt.staffId === user?.staffId;
  // The chairs the edit-series pickers may offer: a manager sees every chair, a non-manager only the
  // series owner (themselves). Falls back to all chairs if staff hasn't loaded yet but the owner row
  // is present in the series.
  const editChairs = useMemo(
    () => (isManager ? allChairs : allChairs.filter((s) => s.id === appt.staffId)),
    [isManager, allChairs, appt.staffId],
  );

  const [editOpen, setEditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  // A detach is irreversible-ish in intent; warn but keep it lightweight (it doesn't delete anything).
  async function detachOne() {
    try {
      await detach.mutateAsync({ seriesId, appointmentId: appt.id });
      toast.success('This date is now a standalone booking — edit it as a normal appointment.');
    } catch {
      toast.error('Could not separate this date from the series.');
    }
  }

  async function confirmCancel() {
    try {
      await cancel.mutateAsync(seriesId);
      toast.success('Remaining bookings cancelled.');
      setCancelOpen(false);
      onClose();
    } catch {
      toast.error('Could not cancel the remaining bookings.');
    }
  }

  const cadence = series ? cadenceLabel(series.intervalWeeks) : null;
  const seriesCancelled = series?.status === 'cancelled';

  return (
    <div
      style={{
        border: '1px solid var(--slm-border)',
        borderRadius: 10,
        padding: '10px 12px',
        background: 'var(--slm-porcelain)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ fontSize: 13.5, fontWeight: 600 }}>Part of a recurring series</div>
      <div className="muted" style={{ fontSize: 12.5 }}>
        {cadence
          ? `This booking repeats ${cadence}. Change just this date, or the whole standing series.`
          : 'This booking is one date in a standing series. Change just this date, or the whole series.'}
      </div>

      {canManage ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Button
            size="sm"
            variant="soft"
            onClick={detachOne}
            disabled={detach.isPending}
            title="Separate this date from the series so a series-wide change won't touch it, then edit it here."
          >
            {detach.isPending ? 'Separating…' : 'Edit just this date'}
          </Button>
          <Button
            size="sm"
            variant="soft"
            onClick={() => setEditOpen(true)}
            disabled={!series || seriesCancelled}
            title={
              seriesCancelled
                ? 'This series has been cancelled.'
                : 'Apply a change to every future booking in this series.'
            }
          >
            Edit the whole series
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setCancelOpen(true)}
            disabled={!series || seriesCancelled}
            title={
              seriesCancelled
                ? 'This series has been cancelled.'
                : 'Cancel every future booking and end the series.'
            }
          >
            Cancel the rest
          </Button>
        </div>
      ) : (
        <span className="muted" style={{ fontSize: 11.5 }}>
          Only the stylist who owns this standing booking can change the series.
        </span>
      )}

      {editOpen && series ? (
        <EditSeriesModal
          series={series}
          tz={seriesTz}
          chairs={editChairs.length ? editChairs : allChairs}
          services={services ?? []}
          onClose={() => setEditOpen(false)}
        />
      ) : null}

      <ConfirmModal
        open={cancelOpen}
        title="Cancel remaining bookings"
        confirmLabel="Cancel remaining"
        // Mirror RecurringSeriesPanel: a typed confirmation for this destructive-with-future-bookings
        // action (brief non-negotiable). Use the client name where present, else a plain "cancel".
        confirmPhrase={
          series?.clientDisplayName?.trim() || appt.clientDisplayName?.trim() || 'cancel'
        }
        busy={cancel.isPending}
        onConfirm={confirmCancel}
        onClose={() => setCancelOpen(false)}
      >
        <p style={{ margin: '0 0 8px' }}>
          This cancels every <strong>future, not-yet-started</strong> booking in{' '}
          <strong>{appt.clientDisplayName || 'this client'}</strong>’s standing series and ends the
          series.
        </p>
        <p style={{ margin: 0 }}>
          Past and in-progress appointments are kept. This can’t be undone — you’d create a new
          standing booking to resume.
        </p>
      </ConfirmModal>
    </div>
  );
}
